import {
  type ClubImportStepName,
  type ClubImportSubsection,
  trackClubImportStepCompleted,
  trackClubImportTutorialViewed,
} from "@/events/clubImportMixpanel";
import { useEffect, useRef } from "react";

export type ClubImportStep = {
  value: ClubImportStepName;
  completed: boolean;
};

/**
 * The view event and the checklist half of the import funnel.
 *
 * Steps are latched rather than watched for a transition: most are already true
 * when the page opens (a Chrome user has "use Chrome" done before they arrive),
 * and a funnel counting only transitions would show all of them failing step 1.
 *
 * The extension check re-polls every second and can flip enabled -> disabled,
 * so the latch is also what stops one flaky poll from double-counting a step.
 */
export const useClubImportFunnel = (
  subsection: ClubImportSubsection,
  steps: ClubImportStep[],
) => {
  const viewed = useRef(false);
  const fired = useRef(new Set<ClubImportStepName>());

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackClubImportTutorialViewed(subsection);
  }, [subsection]);

  // No dependency array: the steps arrive as a fresh array each render and the
  // latch already makes re-runs free.
  useEffect(() => {
    for (const { value, completed } of steps) {
      if (!completed || fired.current.has(value)) continue;
      fired.current.add(value);
      trackClubImportStepCompleted({ subsection, value });
    }
  });
};
